import { useContext, useState, useRef } from "react";
import NavMenuItem from "../ui/nav-menu-item";
import CreateNewTable from "./create-new-table";
import TableContext from "../../contexts/table-context";
import BusinessContext from "../../contexts/business-context";
import { SidebarShimmer } from "../ui/shimmers";
import NavSection from "./nav-section";
import UserContext from "../../contexts/user-context";
import EmptyStateText from "../ui/empty-state-text";
import DropdownOptionItem from "../ui/dropdown-option-item";
import DeleteTableModal from "../ui/delete-table-modal";
import { deleteTable, updateTable } from "../../lib/table";
import { useRouter } from "next/router";
import CreateNewFolder from "../ui/create-new-folder";
import SecondaryDropdownContainer from "../ui/secondary-dropdown-container";
import PrimarySearch from "../ui/primary-search";
import { renderTableIndividualPermission, updateTableIndividualPermission } from "../../lib/table";
import EmptyState from "../ui/empty-state";
import DynamicIcon from "../ui/dynamic-icon";

const TableNav = () => {
    const { tables, fetchTables, duplicateTable } = useContext(TableContext);
    const userCtx = useContext(UserContext);
    const user = userCtx.user;
    const sortedTables = tables ? [...tables].sort((a, b) => b.id - a.id) : [];
    const { activeBusiness, currentUser, setNotificationContent, isGuestUser } = useContext(BusinessContext);
    const router = useRouter();
    const activeTableUuid = router.query.id || (router.asPath.match(/t\/([^/?]+)/)?.[1]);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(null);
    const [openedOption, setOpenedOption] = useState(null);
    const [openedFolders, setOpenedFolders] = useState([]);
    const [isCreateFolderActive, setIsCreateFolderActive] = useState(false);
    const [folderType, setFolderType] = useState(null);
    const [moveToFolder, setMoveToFolder] = useState(false);
    const closeOption = () => { setOpenedOption(null); };
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteModal, setDeleteModal] = useState(null);
    const [draggedTable, setDraggedTable] = useState(null);
    const [dragOverFolder, setDragOverFolder] = useState(null);
    const [dragOverMainArea, setDragOverMainArea] = useState(false);

    const folderContainsActiveTable = (folderId, tablesByFolder) => {
        const children = tablesByFolder[folderId] || [];
        return children.some(t => t.uuid === activeTableUuid);
    };

    const toggleDeleteModal = () => setDeleteModalOpen(prev => !prev);

    const toggleCreateFolder = (sectionName) => {
        setIsCreateFolderActive(!isCreateFolderActive);
        if (!folderType || !isCreateFolderActive) {
            setFolderType(sectionName);
        } else {
            setFolderType(null);
        }
    };

    const handleChangeName = (tableKey, newName) => {
        // tableKey may be "Section:uuid" or plain uuid — extract the real id/uuid
        const tableId = tableKey.includes(':') ? tableKey.split(':').slice(1).join(':') : tableKey;
        const table = tables.find(t => t.id === tableId || t.uuid === tableId);
        if (newName === table?.name) {
            setIsEditing(null); setOpenedOption(null);
            return;
        }
        const name = newName === '' ? '‎' : newName;
        setIsSaving(true);
        updateTable(tableId, { name }, () => { fetchTables(); setIsEditing(null); setOpenedOption(null); setIsSaving(false); }, activeBusiness, (err) => { console.error(err); setIsSaving(false); setIsEditing(null); setOpenedOption(null); }, router);
    };

    const handleDeleteTable = (tableID) => {
        const callBack = () => {
            setOpenedOption(null);
            setDeleteModalOpen(false);
            setDeleteModal(null);
            fetchTables();
            if (router.asPath.includes(`t/${tableID}`)) {
                router.push(`/${router.query.uuid}/home`);
            }
        }

        deleteTable(tableID, callBack, activeBusiness, (err) => console.error(err), router);
    };

    // ── Folder helpers (mirrors table-nav.js) ─────────────────────────────────
    // These are computed after the `if (tables === null)` guard below,
    // so we declare the functions here using the folderEntries/folderById built later.
    // We forward-declare them as closures that reference state only.

    const extractFolderId = (table, existingFolderIds) => {
        if (table?.metadata?.extra?.folder_id && existingFolderIds.includes(table.metadata.extra.folder_id)) {
            return table.metadata.extra.folder_id;
        } else if (table?.metadata?.extra?.folderData) {
            if (Array.isArray(table.metadata.extra.folderData)) {
                const anyFolder = table.metadata.extra.folderData.find(
                    fd => existingFolderIds.includes(fd.id)
                );
                return anyFolder ? anyFolder.id : undefined;
            } else if (
                existingFolderIds.includes(table.metadata.extra.folderData.id)
            ) {
                return table.metadata.extra.folderData.id;
            }
        }
        return undefined;
    };

    const moveTableToFolder = (table, folderId, folderDisplayName) => {
        const updatedMetadata = {
            ...table.metadata,
            extra: {
                ...table.metadata.extra,
                folder_id: undefined,
                folderData: [
                    ...(Array.isArray(table.metadata.extra?.folderData)
                        ? table.metadata.extra.folderData.filter(fd => fd.user !== currentUser?.id)
                        : []),
                    { id: folderId, user: currentUser?.id }
                ]
            }
        };

        // 1. Fetch individual permissions from the folder
        const folderTable = folderEntries.find(f => f?.metadata?.extra?.folderData?.id === folderId);
        if (folderTable) {
            renderTableIndividualPermission(
                folderTable.uuid,
                (folderPermissions) => {
                    // 2. For each permission in the folder, update/add to the moved table
                    folderPermissions.forEach(folderPerm => {
                        renderTableIndividualPermission(
                            table.uuid,
                            (tablePermissions) => {
                                const tablePerm = (tablePermissions || []).find(perm => perm.staff === folderPerm.staff);
                                // If permission doesn't exist or is different, update/add it
                                if (!tablePerm || tablePerm.permission !== folderPerm.permission) {
                                    updateTableIndividualPermission(
                                        table.uuid,
                                        folderPerm.staff,
                                        folderPerm.permission,
                                        activeBusiness,
                                        () => { },
                                        (err) => { console.error(err); },
                                        router
                                    );
                                }
                            },
                            activeBusiness,
                            (err) => { console.error(err); },
                            router
                        );
                    });
                },
                activeBusiness,
                (err) => { console.error(err); },
                router
            );
        }

        setIsSaving(true);
        updateTable(table.uuid, { metadata: updatedMetadata }, () => {
            fetchTables();
            setIsSaving(false);
            setNotificationContent({
                showNotification: true,
                header: 'Move successful',
                message: <div>Table has been moved to <strong>{folderDisplayName}</strong> and permissions synced.</div>,
                icon: 'check',
            });
            setTimeout(() => setNotificationContent(prev => ({ ...prev, showNotification: false })), 5000);
        }, activeBusiness, (err) => { console.error(err); setIsSaving(false); }, router);
    };

    const handleDragStart = (e, table, sectionName) => {
        setDraggedTable({ ...table, draggedFromSection: sectionName });
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', table.uuid);
    };

    const handleDragEnd = () => {
        setDraggedTable(null);
        setDragOverFolder(null);
        setDragOverMainArea(false);
    };

    const handleFolderDragOver = (e, folderId, sectionName) => {
        e.preventDefault();
        e.stopPropagation();
        if (!draggedTable || draggedTable.draggedFromSection !== sectionName) {
            e.dataTransfer.dropEffect = 'none';
            return;
        }
        e.dataTransfer.dropEffect = 'move';
        setDragOverFolder(folderId);
    };

    const handleFolderDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverFolder(null);
        }
    };

    const handleFolderDrop = (e, folderId, sectionName) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverFolder(null);
        if (!draggedTable) return;
        if (draggedTable.draggedFromSection !== sectionName) { setDraggedTable(null); return; }
        // Need existingFolderIds — accessed via closure after the guard below
        const currentFolderId = draggedTable?.metadata?.extra?.folder_id ||
            (Array.isArray(draggedTable?.metadata?.extra?.folderData)
                ? draggedTable.metadata.extra.folderData.find(fd => fd.user === currentUser?.id)?.id
                : draggedTable?.metadata?.extra?.folderData?.id);
        if (currentFolderId === folderId) { setDraggedTable(null); return; }
        const folderEntry = folderEntries?.find(f => f?.metadata?.extra?.folderData?.id === folderId);
        const folderDisplayName = folderEntry?.name || folderEntry?.metadata?.extra?.folderData?.folder_name || '';
        moveTableToFolder(draggedTable, folderId, folderDisplayName);
        setDraggedTable(null);
    };

    const handleMainAreaDragOver = (e, sectionName) => {
        e.preventDefault();
        if (!draggedTable || draggedTable.draggedFromSection !== sectionName) {
            e.dataTransfer.dropEffect = 'none';
            return;
        }
        e.dataTransfer.dropEffect = 'move';
        setDragOverMainArea(sectionName);
    };

    const handleMainAreaDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverMainArea(false);
        }
    };

    const handleMainAreaDrop = (e) => {
        e.preventDefault();
        setDragOverMainArea(false);
        if (!draggedTable) return;
        if (e.target.closest('[data-folder-drop]')) return;
        const hasFolderId = draggedTable?.metadata?.extra?.folder_id;
        const hasFolderData = Array.isArray(draggedTable?.metadata?.extra?.folderData) &&
            draggedTable.metadata.extra.folderData.some(fd => fd.user === currentUser?.id);
        if (!hasFolderId && !hasFolderData) { setDraggedTable(null); return; }
        handleRemoveFromFolder(draggedTable);
        setDraggedTable(null);
    };

    const handleRemoveFromFolder = (table) => {
        const updatedMetadata = {
            ...table.metadata,
            extra: {
                ...table.metadata.extra,
                folder_id: undefined,
                folderData: Array.isArray(table.metadata.extra?.folderData)
                    ? table.metadata.extra.folderData.filter(fd => fd.user !== currentUser?.id)
                    : undefined
            }
        };
        setIsSaving(true);
        updateTable(table.uuid, { metadata: updatedMetadata }, () => {
            fetchTables();
            setIsSaving(false);
            setNotificationContent({
                showNotification: true,
                header: 'Move successful',
                message: <div>Table has been moved out of folder.</div>,
                icon: 'check',
            });
            setTimeout(() => setNotificationContent(prev => ({ ...prev, showNotification: false })), 5000);
        }, activeBusiness, (err) => { console.error(err); setIsSaving(false); }, router);
    };

    if (tables === null) {
        return <NavSection><div className="grid gap-[4px] content-start relative"><SidebarShimmer /></div></NavSection>;
    }

    // ── Separate folder entries from regular tables ───────────────────────────
    const folderEntries = sortedTables.filter(t => t?.metadata?.extra?.folderData?.is_folder === true);
    const regularTables = sortedTables.filter(t => t?.metadata?.extra?.folderData?.is_folder !== true);

    // ── Flat list of folderData objects (for name validation in CreateNewFolder) ─
    const folderDataList = folderEntries
        .map(f => f?.metadata?.extra?.folderData)
        .filter(Boolean);
    const folderNames = folderDataList.map(fd => fd.folder_name);

    // ── Section classification ─────────────────────────────────────────────────
    const isPrivate = t => t.permission.charAt(1) === '0' && t.owner?.user.email === user?.email;
    const isTeam = t => t.permission.charAt(1) !== '0';
    const isShared = t => t.permission.charAt(1) === '0' && t.owner?.user.email !== user?.email;

    // ── Build lookup: folderId → folder table entry ───────────────────────────
    const folderById = {};
    folderEntries.forEach(f => {
        const fd = f?.metadata?.extra?.folderData;
        if (fd?.id) folderById[fd.id] = f;
    });

    const existingFolderIds = folderDataList.map(fd => fd.id);



    const DropDownContent = ({ table, sectionName = '' }) => {
        const currentFolderId = extractFolderId(table, existingFolderIds);
        const isInFolder = currentFolderId !== undefined && currentFolderId !== null;
        const isFolder = table?.metadata?.extra?.folderData?.is_folder;
        const isTableOwner = table.owner?.id === currentUser?.id;
        const availableFolders = folderEntries

        const privateFolders = folderEntries.filter(fd => fd.permission === "700" && fd.owner.id === currentUser?.id);
        const sharedFolders = folderEntries.filter(fd => fd.permission === "700" && fd.owner.id !== currentUser?.id);
        const teamFolders = folderEntries.filter(fd => fd.permission === "760");
        const sectionAvailableFolders = sectionName === 'Private' ? privateFolders
            : sectionName === 'Shared' ? sharedFolders
                : sectionName === 'Team' ? teamFolders
                    : [];

        const moveToFolderButtonRef = useRef(null);
        const [filteredFolders, setFilteredFolders] = useState(sectionAvailableFolders);

        const onSearchFolders = (value) => {
            setFilteredFolders(sectionAvailableFolders.filter(fd =>
                fd.folder_name.toLowerCase().includes(value.toLowerCase())
            ));
        };

        const handleShareFolder = () => {
            router.push(`/${activeBusiness?.uuid}/t/${table.uuid}/?share_folder=true`);
        }

        const isPinned = table.metadata?.extra?.pinned_by?.includes(currentUser?.id);

        const handlePinTable = (table) => {
            const callBack = () => {
                fetchTables();
                setOpenedOption(null);
            }
            const updatedMetadata = {
                metadata: {
                    ...table.metadata,
                    extra: {
                        ...table.metadata.extra,
                        pinned_by: isPinned
                            ? (table.metadata.extra?.pinned_by || []).filter(id => id !== currentUser?.id)
                            : [...(table.metadata.extra?.pinned_by || []), currentUser?.id]
                    }
                }
            };
            updateTable(table.uuid, updatedMetadata, callBack, activeBusiness, (json) => console.error(json), router);
        }


        return (
            <div onClick={(e) => e.stopPropagation()} className="grid gap-[4px] content-start">
                {isTableOwner && <DropdownOptionItem icon="pen-line" additionalclasses="!py-[4px]" onClick={() => setIsEditing(`${sectionName}:${table.uuid}`)}>Rename</DropdownOptionItem>}
                <DropdownOptionItem icon={isPinned ? 'pin-off' : 'pin'} onClick={() => handlePinTable(table)}>{isPinned ? `Unpin this ${isFolder ? 'folder' : 'table'}` : `Pin this ${isFolder ? 'folder' : 'table'}`}</DropdownOptionItem>
                {!isFolder && (
                    <>
                        <DropdownOptionItem icon="copy-plus" additionalclasses="!py-[4px]" onClick={() => { duplicateTable(table); setOpenedOption(null); }}>Duplicate</DropdownOptionItem>
                        <div className="relative">
                            {isTableOwner &&
                                <DropdownOptionItem
                                    ref={moveToFolderButtonRef}
                                    icon="folder-plus"
                                    additionalclasses={`!py-[4px] ${sectionAvailableFolders.length === 0 ? 'opacity-50' : ''}`}
                                    onClick={() => setMoveToFolder(!moveToFolder)}
                                    isDisabled={sectionAvailableFolders.length === 0}
                                >
                                    Move to folder
                                </DropdownOptionItem>
                            }
                            <SecondaryDropdownContainer
                                isOpen={moveToFolder}
                                toggleDropdown={() => setMoveToFolder(!moveToFolder)}
                                parentRef={moveToFolderButtonRef}
                                additionalclasses="!min-w-[181px] !top-6 !left-[180px]"
                            >
                                <PrimarySearch placeholder="Search folders" onSearch={onSearchFolders} additionalclasses="!w-[181px]" />
                                {filteredFolders.length > 0 ? (
                                    filteredFolders.map(fd => (
                                        <DropdownOptionItem
                                            key={fd.id}
                                            icon="folder"
                                            additionalclasses="!py-[4px]"
                                            onClick={() => { moveTableToFolder(table, fd?.metadata?.extra?.folderData.id, (fd.name || fd?.metadata?.extra?.folderData.folder_name)); setOpenedOption(null); setMoveToFolder(false); }}
                                        >
                                            {fd.name || fd?.metadata?.extra?.folderData.folder_name}
                                        </DropdownOptionItem>
                                    ))
                                ) : (
                                    <div className="m-auto"><EmptyState>No folders found</EmptyState></div>
                                )}
                            </SecondaryDropdownContainer>
                        </div>
                    </>
                )}
                {isInFolder && !isFolder && isTableOwner && (
                    <DropdownOptionItem icon="folder-x" additionalclasses="!py-[4px]" onClick={() => { handleRemoveFromFolder(table); setOpenedOption(null); }}>Remove from folder</DropdownOptionItem>
                )}
                {isFolder && (
                    <DropdownOptionItem icon='share' additionalclasses="!py-[4px]" onClick={() => { handleShareFolder(); setOpenedOption(null) }}>Share folder</DropdownOptionItem>
                )}
                {isTableOwner && (
                    <DropdownOptionItem icon="trash-2" additionalclasses="hover:custom-text-danger-500 !py-[4px]" onClick={(e) => { e.stopPropagation(); setDeleteModal(table.uuid); setOpenedOption(null); toggleDeleteModal(); }}>Delete</DropdownOptionItem>
                )}
            </div>
        );
    };

    const getTablesInFolder = (folderUuid) => {
        if (!folderUuid) return [];
        const folderTableEntry = folderEntries.find(f => f.uuid === folderUuid);
        if (!folderTableEntry) return [];
        const folderDataId = folderTableEntry?.metadata?.extra?.folderData?.id;
        if (!folderDataId) return [];
        return regularTables.filter(t => {
            const tFolderId = extractFolderId(t, existingFolderIds);
            return tFolderId === folderDataId;
        });
    }

    const renderTable = (table, sectionName = '') => {
        const optionKey = `${sectionName}:${table.uuid}`;
        return (
            <div
                key={optionKey}
                draggable
                onDragStart={(e) => handleDragStart(e, table, sectionName)}
                onDragEnd={handleDragEnd}
                className={`${draggedTable?.uuid === table.uuid ? 'opacity-50' : ''} cursor-move`}
            >
                <NavMenuItem
                    dropdownContent={<DropDownContent table={table} sectionName={sectionName} />}
                    toggle isSaving={isSaving}
                    toggleEditing={() => setIsEditing(null)}
                    handleChangeName={handleChangeName}
                    tableId={table.uuid}
                    isEditing={isEditing === optionKey}
                    closeEllipsis={closeOption}
                    handleEllipsis={() => { setOpenedOption(optionKey); setMoveToFolder(false); }}
                    isEllipsisOpen={openedOption === optionKey}
                    chevron
                    icon={table?.metadata?.render_types?.[0]?.type === 'document' ? 'file' : 'table'}
                    href={`/t/${table.uuid}`}
                >
                    {table.name}
                </NavMenuItem>
                <DeleteTableModal handleDeleteTable={handleDeleteTable} table={table} deleteModalOpen={deleteModalOpen && table.uuid === deleteModal} toggleDeleteModal={toggleDeleteModal} getTablesInFolder={(tableId) => getTablesInFolder(tableId)} />
            </div>
        );
    };

    const pinnedFolders = folderEntries.filter(f => f.metadata?.extra?.pinned_by?.includes(currentUser?.id));
    const pinnedTables = regularTables.filter(t => t.metadata?.extra?.pinned_by?.includes(currentUser?.id));
    const hasPinned = pinnedFolders.length > 0 || pinnedTables.length > 0;

    const renderPinnedSection = () => {
        if (!hasPinned) return null;

        return (
            <>
                <div className="grid mt-[20px] pt-[8px] pb-[16px] border-t-[1px] custom-border-grey-50">
                    <CreateNewTable toggleCreate={() => toggleCreateFolder('Private')} />
                </div>
                <NavSection section="Pinned">
                    <div className="grid gap-[4px] content-start relative">
                        {pinnedFolders.map(folderTable => {
                            const fd = folderTable?.metadata?.extra?.folderData;
                            if (!fd?.id) return null;
                            const folderKey = `Pinned:${fd.id}`;
                            const optionKey = `Pinned:${folderTable.uuid}`;
                            const isOpen = openedFolders.includes(folderKey);
                            const folderName = folderTable?.name || fd.folder_name;
                            const isTableOwner = folderTable.owner?.id === currentUser?.id;
                            const sectionLabel = folderTable.permission.charAt(1) !== '0' ? 'Team'
                                : folderTable.owner?.user.email === user?.email ? 'Private' : 'Shared';

                            // Get children for this folder in the pinned section
                            const children = regularTables.filter(t => extractFolderId(t, existingFolderIds) === fd.id);
                            const isActive = fd.id === activeTableUuid || children.some(t => t.uuid === activeTableUuid);

                            return (
                                <div key={`pinned-folder-${folderTable.uuid}`} className="grid gap-[4px]">
                                    <NavMenuItem
                                        dropdownContent={<DropDownContent table={folderTable} sectionName={sectionLabel} />}
                                        isSaving={isSaving}
                                        toggleEditing={() => setIsEditing(null)}
                                        handleChangeName={handleChangeName}
                                        tableId={optionKey}
                                        isEditing={isEditing === optionKey}
                                        closeEllipsis={closeOption}
                                        active={isActive}
                                        handleEllipsis={isTableOwner ? () => { setOpenedOption(optionKey); setMoveToFolder(false); } : null}
                                        isEllipsisOpen={openedOption === optionKey}
                                        icon={isOpen ? 'folder-open' : 'folder'}
                                        onClick={() => setOpenedFolders(prev =>
                                            isOpen ? prev.filter(id => id !== folderKey) : [...prev, folderKey]
                                        )}
                                    >
                                        {folderName}
                                    </NavMenuItem>
                                    {isOpen && children.length > 0 && (
                                        <div className="ml-[16px] grid gap-[4px] content-start mt-[4px]">
                                            {children.map(t => renderTable(t, 'Pinned'))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {pinnedFolders.length > 0 && pinnedTables.length > 0 && (
                            <div className="border-b-[0.5px] custom-border-grey-400 opacity-10 my-[4px]" />
                        )}
                        {pinnedTables.map(t => {
                            return renderTable(t, 'Pinned');
                        })}
                    </div>
                </NavSection>
            </>
        );
    };

    const renderSection = (sectionLabel, filterFn, showCreateNew = false) => {
        // ── Per-section folder/table grouping (mirrors table-nav.js) ──────────
        const sectionFolders = folderEntries.filter(filterFn);
        const sectionFolderIds = sectionFolders.map(f => f?.metadata?.extra?.folderData?.id).filter(Boolean);

        const sectionRegularTables = regularTables.filter(filterFn);

        // Tables whose folder exists in THIS section
        const tablesWithValidFolders = sectionRegularTables.filter(t => {
            const folderId = extractFolderId(t, existingFolderIds);
            return folderId != null && sectionFolderIds.includes(folderId);
        });

        // Tables with no folder, or whose folder is in another section
        const tablesWithNoFolder = sectionRegularTables.filter(t => {
            const folderDataArr = Array.isArray(t?.metadata?.extra?.folderData)
                ? t.metadata.extra.folderData : [];
            const hasFolder = folderDataArr.some(fd => existingFolderIds.includes(fd.id));
            const hasLegacyFolder = existingFolderIds.includes(t?.metadata?.extra?.folder_id);
            if (!hasFolder && !hasLegacyFolder) return true;
            const folderId = extractFolderId(t, existingFolderIds);
            if (folderId && !sectionFolderIds.includes(folderId)) return true;
            return false;
        });

        // Group tables by folder ID
        const tablesByFolder = tablesWithValidFolders.reduce((acc, t) => {
            const folderId = extractFolderId(t, existingFolderIds);
            if (folderId) {
                if (!acc[folderId]) acc[folderId] = [];
                acc[folderId].push(t);
            }
            return acc;
        }, {});

        if (sectionLabel !== 'Private' && sectionFolders.length === 0 && tablesWithNoFolder.length === 0) return null;

        const showFolderCreator = !isGuestUser && (
            sectionLabel === 'Team' ||
            (isCreateFolderActive && folderType === sectionLabel)
        );

        return (
            <>
                {showCreateNew && !hasPinned &&
                    <div className="grid mt-[20px] pt-[8px] pb-[16px] border-t-[1px] custom-border-grey-50">
                        <CreateNewTable toggleCreate={() => toggleCreateFolder(sectionLabel)} />
                    </div>
                }
                <NavSection section={sectionLabel}>
                    <div
                        className="grid gap-[4px] content-start relative"
                        onDragOver={(e) => handleMainAreaDragOver(e, sectionLabel)}
                        onDragLeave={handleMainAreaDragLeave}
                        onDrop={handleMainAreaDrop}
                    >
                        {showFolderCreator && (
                            <>
                                <CreateNewFolder
                                    folderNames={folderNames}
                                    isCreateActive={isCreateFolderActive && folderType === sectionLabel}
                                    toggleCreate={() => toggleCreateFolder(sectionLabel)}
                                    folderPermission={sectionLabel.toLowerCase()}
                                />
                                <div className="border-b-[0.5px] custom-border-grey-400 opacity-10 my-[4px]" />
                            </>
                        )}
                        {sectionFolders.length === 0 && tablesWithNoFolder.length === 0
                            ? <EmptyStateText>No table yet</EmptyStateText>
                            : <>
                                {[...sectionFolders]
                                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                    .map(folderTable => {
                                        const fd = folderTable?.metadata?.extra?.folderData;
                                        if (!fd?.id) return null;
                                        const isOpen = openedFolders.includes(fd.id);
                                        const folderName = folderTable?.name || fd.folder_name;
                                        const children = tablesByFolder[fd.id] ?? [];
                                        const isActive = fd.id === activeTableUuid || folderContainsActiveTable(fd.id, tablesByFolder);
                                        const isTableOwner = folderTable.owner?.id === currentUser?.id;

                                        return (
                                            <div key={folderTable.uuid} className="grid gap-[4px]">
                                                <div
                                                    data-folder-drop="true"
                                                    onDragOver={(e) => handleFolderDragOver(e, fd.id, sectionLabel)}
                                                    onDragLeave={handleFolderDragLeave}
                                                    onDrop={(e) => handleFolderDrop(e, fd.id, sectionLabel)}
                                                    className={`${dragOverFolder === fd.id ? 'custom-bg-grey-200 bg-opacity-50 rounded-[8px]' : ''} transition-all duration-200 grid gap-[4px]`}
                                                >
                                                    <NavMenuItem
                                                        dropdownContent={<DropDownContent table={folderTable} sectionName={sectionLabel} />}
                                                        isSaving={isSaving}
                                                        toggleEditing={() => setIsEditing(null)}
                                                        handleChangeName={handleChangeName}
                                                        tableId={folderTable.uuid}
                                                        isEditing={isEditing === folderTable.uuid}
                                                        closeEllipsis={closeOption}
                                                        active={isActive}
                                                        handleEllipsis={isTableOwner ? () => { setOpenedOption(folderTable.uuid); setMoveToFolder(false); } : null}
                                                        isEllipsisOpen={openedOption === folderTable.uuid}
                                                        icon={isOpen ? 'folder-open' : 'folder'}
                                                        onClick={() => setOpenedFolders(prev =>
                                                            isOpen ? prev.filter(id => id !== fd.id) : [...prev, fd.id]
                                                        )}
                                                    >
                                                        {folderName}
                                                    </NavMenuItem>
                                                    {isOpen && children.length > 0 && (
                                                        <div className="ml-[16px] grid gap-[4px] content-start mt-[4px]">
                                                            {children.map(t => renderTable(t, sectionLabel))}
                                                        </div>
                                                    )}
                                                </div>
                                                <DeleteTableModal handleDeleteTable={handleDeleteTable} table={folderTable} deleteModalOpen={deleteModalOpen && folderTable.uuid === deleteModal} toggleDeleteModal={toggleDeleteModal} folder={fd} getTablesInFolder={() => getTablesInFolder(folderTable.uuid)} />
                                            </div>
                                        );
                                    })}
                                {sectionFolders.length > 0 && tablesWithNoFolder.length > 0 &&
                                    <div className="border-b-[0.5px] custom-border-grey-400 opacity-10 my-[4px]" />
                                }
                                {[...tablesWithNoFolder]
                                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                    .map(t => renderTable(t, sectionLabel))}
                            </>
                        }
                    </div>
                </NavSection>
            </>
        );
    };

    return (
        <>
            {renderPinnedSection()}
            {renderSection('Private', isPrivate, true)}
            {renderSection('Shared', isShared, false)}
            {renderSection('Team', isTeam, false)}
        </>
    );
};

export default TableNav;